/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import LegalPageLayout from './LegalPageLayout';
import { useAuth } from '@/lib/auth';
import { LEGAL_OWNER } from '@/lib/legalOwner';
import { EULA_CHECKBOX_LABEL, EULA_TEXT, EULA_TITLE, EULA_VERSION, IP_FOOTER_NOTICE, IP_WATERMARK_TEXT } from '@/config/ipProtection';
import { ExportHistory, ReleaseVerifyCard, TsrVerifyCard } from '@/components/legal/LegalProofTools';
import { AcceptancesRegister } from '@/components/legal/AcceptancesRegister';

export default function LicensePage() {
  const { user } = useAuth();
  const [historyKey, setHistoryKey] = useState(0);
  return (
    <LegalPageLayout
      activeKey="license"
      title="Licenza, proprietà intellettuale e Termini di Riservatezza"
      subtitle="Chi detiene i diritti su SanitàNexus White-Label, cosa è vietato e come vengono registrate le accettazioni della demo."
      lastUpdated={LEGAL_OWNER.lastReview}
    >
      <h2>1. Titolarità dei diritti</h2>
      <p data-testid="license-owner">
        SanitàNexus White-Label — codice sorgente, architettura software, logiche <em>Local-First</em>, integrazione dell'AI locale (WebLLM/Ollama),
        layout e interfaccia grafica, configurazioni dei prompt e documentazione — è opera dell'ingegno di <strong>{LEGAL_OWNER.fullName}</strong>
        {' '}(C.F. <span className="font-mono">{LEGAL_OWNER.fiscalCode}</span>), autore e unico sviluppatore, ed è protetta dalla <strong>Legge 22 aprile 1941, n. 633</strong> e dai trattati internazionali in materia di diritto d'autore.
      </p>
      <div className="not-prose rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm" data-testid="license-copyright-line">{IP_FOOTER_NOTICE}</div>

      <h2>2. Cosa è espressamente vietato</h2>
      <ul>
        <li>copia, riproduzione o ridistribuzione, anche parziale, del codice sorgente o della build;</li>
        <li>reverse engineering, decompilazione, estrazione delle logiche <em>Local-First</em>, delle catene di hash audit o delle configurazioni dei prompt AI;</li>
        <li>imitazione dei layout UI/UX, dei flussi (Installer Wizard, Portale Paziente cifrato, workflow referti) per lo sviluppo di software concorrenti;</li>
        <li>rimozione o alterazione delle intestazioni di copyright, delle filigrane e degli avvisi proprietari presenti nel codice e nell'interfaccia.</li>
      </ul>

      <h2>3. {EULA_TITLE}</h2>
      <p>Al primo accesso alla demo ogni utente non amministratore deve accettare i seguenti termini spuntando “{EULA_CHECKBOX_LABEL}” (versione {EULA_VERSION}):</p>
      <blockquote data-testid="license-eula-text">{EULA_TEXT}</blockquote>
      <p>L'accettazione viene registrata con data/ora UTC, identità dell'utente, indirizzo IP e <em>user agent</em> in un <strong>audit log immutabile</strong> in cui ogni evento è concatenato al precedente tramite impronta SHA-256. L'amministratore può esportare il registro in PDF/JSON e sottoporne l'impronta a marcatura temporale qualificata eIDAS o ad ancoraggio blockchain.</p>

      <h2>4. Misure tecniche di protezione</h2>
      <ul>
        <li>intestazione proprietaria in ogni file sorgente e header HTTP <code>X-Copyright</code> su ogni risposta del server;</li>
        <li>filigrana “{IP_WATERMARK_TEXT}” su accesso, installer e area amministrazione; nelle sessioni demo la filigrana include l'identità dell'utente collegato, così ogni schermata è riconducibile;</li>
        <li>protezioni anti-manomissione lato client per visitatori e account demo/paziente (menu contestuale, scorciatoie DevTools, selezione testo), disattivate per l'amministratore;</li>
        <li>registro accettazioni EULA nella catena audit SHA-256 verificabile.</li>
      </ul>

      <h2>5. Licenza d'uso per le strutture acquirenti</h2>
      <p>La licenza White-Label concede alla struttura sanitaria acquirente il diritto non esclusivo e non trasferibile di installare ed utilizzare il software sulla propria infrastruttura, con il proprio brand, secondo i <a href="/legal/terms">Terms of Service</a>. Non trasferisce alcun diritto di proprietà intellettuale sul codice, sull'architettura o sui contenuti. {LEGAL_OWNER.jurisdiction}.</p>

      {user?.is_admin ? (
        <>
          <hr />
          <AcceptancesRegister onExported={() => setHistoryKey((k) => k + 1)} />
          <hr />
          <ExportHistory refreshKey={historyKey} />
          <hr />
          <ReleaseVerifyCard />
          <hr />
          <TsrVerifyCard />
        </>
      ) : (
        <div className="not-prose mt-8 rounded-xl border border-border bg-muted/40 p-4 text-xs text-muted-foreground flex items-start gap-2" data-testid="license-admin-hint">
          <FileText className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>Il registro delle accettazioni e l'export della prova legale (PDF firmato SHA-256) sono visibili solo all'amministratore autenticato.</span>
        </div>
      )}
    </LegalPageLayout>
  );
}
